import { AutoAwesome, Close as CloseIcon } from 'components/icons';
import { usePostHog } from '@posthog/react';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useIsCreateDialogMinimised, useIsCreateDialogOpen } from 'components/create-dialog/CreateDialogContext';
import { Tooltip } from 'components/ui/tooltip';
import { useDispatch, useSelector } from 'store';
import { floatingAiAutoResumed, setFloatingAiVisibility, switchFloatingToOwnSession } from 'store/reducers/aiChat';
import { selectIsRibbonOnScreen } from 'store/reducers/exports';
import { FeatureFlag } from 'types/features';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { isResumeFresh, readResumeRecord } from '../aiChatResume';
import { useAiChatSurface } from '../AiChatSessionProvider';
import { useAiChatDeleteThreadPrompt } from '../hooks/useAiChatDeleteThreadPrompt';
import FloatingAiPanel from './FloatingAiPanel';

// The panel reveal's way out, `--panel-close-dur` in index.css.
const CLOSE_ANIMATION_MS = 250;

// The panel is presentational; the conversation is the layout-level shared session, so it survives
// both opening/closing the panel and route changes. Visibility lives in the store because the full
// AI page opens the panel from outside this subtree when an entity link navigates away from it.
const FloatingAiAssistantInner = ({ showLauncher, prefersSideDock }: Required<Props>) => {
    const intl = useIntl();
    const posthog = usePostHog();
    const dispatch = useDispatch();
    const visibility = useSelector((state) => state.aiChat.floatingVisibility);
    const sessionKey = useSelector((state) => state.aiChat.floatingSessionKey);
    const isRibbonOnScreen = useSelector(selectIsRibbonOnScreen);
    const isCreateDialogMinimised = useIsCreateDialogMinimised();
    // The create dialog, its phone sheet and the classic Timecard center sit over the panel. The new design never
    // shows that drawer and the panel together.
    const isUnderDialog = useIsCreateDialogOpen();
    const isTimecardCenterOpen = useSelector((state) => state.subDrawer.activeDrawerId === 'timecard-center');
    // What the panel renders: its own conversation, or the one `/ai-chat` handed over.
    const { controller, pageContext } = useAiChatSurface('floating', sessionKey);
    const { controller: ownController } = useAiChatSurface('floating');

    // Switching conversations from the panel always acts on the panel's own session, so browsing
    // history here never rewrites what `/ai-chat` is holding.
    const handleSelectThread = (threadId: number) => {
        dispatch(switchFloatingToOwnSession());
        ownController.selectThread(threadId);
    };

    const handleNewChat = () => {
        dispatch(switchFloatingToOwnSession());
        ownController.newChat();
    };

    // Deleting acts on the session on screen: if that is the thread being deleted, the controller
    // clears it back to a new chat on its own.
    const { deleteThread } = controller;
    // The prompt is scoped to the panel: it blurs and blocks the panel, not the page around it.
    const panelRef = useRef<HTMLDivElement>(null);
    const handleDeleteThread = useAiChatDeleteThreadPrompt(deleteThread, { container: panelRef });

    useEffect(() => {
        if (visibility !== 'closing') {
            return;
        }
        const timer = setTimeout(() => dispatch(setFloatingAiVisibility('closed')), CLOSE_ANIMATION_MS);
        return () => clearTimeout(timer);
    }, [dispatch, visibility]);

    // Auto-resume picks the thread without the user asking for it, so a thread that no longer
    // loads — deleted elsewhere, a transient failure — would leave the panel on an empty
    // conversation with nothing to explain it. Fall back to a new chat, which also drops the
    // record so the next open does not try again.
    const { isHistoryError, newChat: newOwnChat } = ownController;
    useEffect(() => {
        if (isHistoryError) {
            newOwnChat();
        }
    }, [isHistoryError, newOwnChat]);

    // Which visibility the open-time policy last saw; the effect below re-runs on unrelated
    // renders, and the policy must fire once per opening, not once per render.
    const lastSeenVisibilityRef = useRef(visibility);

    // The five-minute rule, applied when the panel opens (from any opener — the launch button, an
    // empty state's "ask AI" link): resume the recent conversation, otherwise start fresh. Two
    // things are never interrupted, regardless of the clock: a reply still streaming, and a
    // conversation handed over from the AI page.
    useEffect(() => {
        const previousVisibility = lastSeenVisibilityRef.current;
        lastSeenVisibilityRef.current = visibility;
        if (visibility !== 'open' || previousVisibility === 'open') {
            return;
        }
        if (sessionKey === 'page' || ownController.isStreaming) {
            return;
        }

        const record = readResumeRecord('floating');
        const shouldResume = isResumeFresh(record);
        posthog.capture('ai_chat_resume_policy', {
            surface: 'floating',
            resumed: shouldResume,
            idle_seconds: record ? Math.round((Date.now() - record.lastActiveAt) / 1000) : null,
        });

        const isSessionEmpty = ownController.threadId == null && ownController.userVisibleMessages.length === 0;
        if (shouldResume) {
            // Only a conversation that is not already on screen "appears" — a session still live
            // in memory keeps rendering as-is, with no banner to explain it.
            if (isSessionEmpty) {
                ownController.selectThread(record.threadId);
                dispatch(floatingAiAutoResumed({ threadId: record.threadId, lastActiveAt: record.lastActiveAt }));
            }
        } else if (!isSessionEmpty) {
            ownController.newChat();
        }
    }, [dispatch, ownController, posthog, sessionKey, visibility]);

    const isOpen = visibility === 'open';
    const label = intl.formatMessage({ id: isOpen ? 'ai-chat.floating.close' : 'ai-chat.floating.fab-label' });

    return (
        <>
            {visibility !== 'closed' ? (
                <FloatingAiPanel
                    ref={panelRef}
                    controller={controller}
                    pageContext={pageContext}
                    isHandoff={sessionKey === 'page'}
                    reserveBottomRow={showLauncher || isRibbonOnScreen || isCreateDialogMinimised}
                    isUnderDialog={isUnderDialog || isTimecardCenterOpen}
                    prefersSideDock={prefersSideDock}
                    onSelectThread={handleSelectThread}
                    onNewChat={handleNewChat}
                    onDeleteThread={handleDeleteThread}
                    isClosing={visibility === 'closing'}
                    onClose={() => dispatch(setFloatingAiVisibility('closing'))}
                />
            ) : null}
            {/* Sits immediately left of the global "+" button (24px off the page's right edge and the window's
                bottom, 62px across), matching its size so the two read as one row of floating actions. Both move
                left of the AI panel docked to the side. */}
            {showLauncher && (
                <Tooltip content={label} side="left">
                    <button
                        type="button"
                        onClick={() => {
                            if (!isOpen) {
                                posthog.capture('ai_chat_opened', { surface: 'floating' });
                            }
                            dispatch(setFloatingAiVisibility(isOpen ? 'closing' : 'open'));
                        }}
                        aria-label={label}
                        aria-expanded={isOpen}
                        className="bg-info hover:bg-info-light fixed right-[calc(102px+var(--ai-side-dock-width,0px))] bottom-6 z-(--z-floating-ai-launcher) flex size-[62px] cursor-pointer items-center justify-center rounded-full border-0 text-white shadow-[0_8px_22px_rgba(111,111,240,0.45)] transition-transform motion-safe:hover:scale-105"
                    >
                        {isOpen ? <CloseIcon className="text-[24px]" /> : <AutoAwesome className="text-[24px]" />}
                        {/* The session outlives the panel, so a reply can still be arriving with
                            nothing else on screen to say so. */}
                        {!isOpen && controller.isStreaming ? (
                            <span
                                className="bg-secondary absolute end-1 top-1 size-3 animate-pulse rounded-full border-2 border-white motion-reduce:animate-none"
                                aria-hidden
                            />
                        ) : null}
                    </button>
                </Tooltip>
            )}
        </>
    );
};

const READ_CRESPECT_AI = ['READ.CRESPECT_AI'];

/** Whether the floating panel is there to open: the feature flag and READ.CRESPECT_AI. */
export function useFloatingAiAvailable(): boolean {
    const isEnabled = useIsFeatureEnabled(FeatureFlag.AI_CHAT_ENABLED);
    const hasPermission = useHasPermissions(READ_CRESPECT_AI);
    return isEnabled && hasPermission;
}

type Props = {
    /** Draw the round launcher; the panel docked bottom right stops above it. Without it the panel opens only from elsewhere. */
    showLauncher?: boolean;
    /** Show a corner panel in the side dock, beside the page: a timecard form's answer box lies under it otherwise. */
    prefersSideDock?: boolean;
};

/** Global floating Crespect AI surface, gated on the feature flag + READ.CRESPECT_AI permission. */
const FloatingAiAssistant = ({ showLauncher = true, prefersSideDock = false }: Props) => {
    if (!useFloatingAiAvailable()) {
        return null;
    }

    return <FloatingAiAssistantInner showLauncher={showLauncher} prefersSideDock={prefersSideDock} />;
};

export default FloatingAiAssistant;
